import type { MenuProps } from 'antd';
import { Dropdown, Modal } from 'antd';
import {
  DeleteOutlined,
  MoreOutlined,
  RedoOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { useState } from 'react';
import { useStore } from '../../helpers/use-store';
import { LoadingState, useLoading } from '@monorepo/react-components';
import { useNotification } from '../../hooks/use-notification';
import { SubscriptionStatusName } from '@monorepo/types';
import { usePollUserState } from '../../hooks/use-poll-user-state';
import { useTranslation } from 'react-i18next';

type ActionType = 'cancel' | 'renew';

const SubscriptionActions = () => {
  const { t } = useTranslation();
  const {
    dataStore: { userStore },
  } = useStore();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [actionType, setActionType] = useState<ActionType>('cancel');
  const { loadingState, updateLoadingState } = useLoading();
  const notificationApi = useNotification();
  const { startPolling } = usePollUserState();

  const handleAction = async () => {
    try {
      updateLoadingState(LoadingState.Loading);

      if (actionType === 'cancel') {
        await userStore.cancelSubscription();
      } else {
        await userStore.renewSubscription();
      }

      // Provider webhook is async — poll until the new sub state lands.
      startPolling();

      notificationApi.success({
        placement: 'top',
        message: t('common.success'),
        description:
          actionType === 'cancel'
            ? t('billing.actions.cancelSuccess')
            : t('billing.actions.renewSuccess'),
      });
    } catch (e) {
      console.error(`failed ${actionType}ing subscription`, e);
      notificationApi.error({
        placement: 'top',
        message: t('common.error'),
        description:
          actionType === 'cancel'
            ? t('billing.actions.cancelError')
            : t('billing.actions.renewError'),
      });
    } finally {
      updateLoadingState(LoadingState.Loaded);
      setIsModalOpen(false);
    }
  };

  const openModal = (type: ActionType) => {
    setActionType(type);
    setIsModalOpen(true);
  };

  const manageSubscription = async () => {
    try {
      const result = await userStore.getCustomerPortal();

      if (result.url) {
        // Redirect-style portals (Polar). Open in a new tab so the user can
        // come back to the app without losing state.
        window.open(result.url, '_blank', 'noopener,noreferrer');
        return;
      }

      // Providers without a redirect portal (PayPro) — fall back to the
      // email magic-link flow.
      notificationApi.info({
        placement: 'top',
        message: t('billing.actions.checkEmailTitle'),
        description: t('billing.actions.checkEmailDesc'),
      });
    } catch (e) {
      console.error('failed opening customer portal', e);
      notificationApi.error({
        placement: 'top',
        message: t('billing.actions.portalErrorTitle'),
        description: t('billing.actions.portalErrorDesc'),
      });
    }
  };

  const items: MenuProps['items'] = [
    {
      key: 'cancel',
      disabled:
        userStore.userSubscription.status !== SubscriptionStatusName.Active,
      label: (
        <span onClick={() => openModal('cancel')}>
          <DeleteOutlined style={{ marginRight: 8 }} />
          {t('billing.actions.cancel')}
        </span>
      ),
      danger: true,
    },
    {
      key: 'renew',
      disabled:
        userStore.userSubscription.status !== SubscriptionStatusName.Suspended,
      label: (
        <span onClick={() => openModal('renew')}>
          <RedoOutlined style={{ marginRight: 8 }} />
          {t('billing.actions.renew')}
        </span>
      ),
    },
    {
      key: 'manageSubscription',
      disabled: !userStore.userSubscription.subscriptionId,
      label: (
        <span onClick={manageSubscription}>
          <SettingOutlined style={{ marginRight: 8 }} />
          {t('billing.actions.manageSubscription')}
        </span>
      ),
    },
  ];

  const modalContent = {
    cancel: {
      title: t('billing.actions.cancelTitle'),
      okText: t('billing.actions.confirmCancel'),
      cancelText: t('billing.actions.noKeep'),
      content: (
        <>
          <p>{t('billing.actions.cancelModalContent')}</p>
          <ul>
            <li>{t('billing.actions.cancelLine1')}</li>
          </ul>
          <p>{t('billing.actions.cancelLine2')}</p>
        </>
      ),
    },
    renew: {
      title: t('billing.actions.renewTitle'),
      okText: t('billing.actions.confirmRenew'),
      cancelText: t('billing.actions.noThanks'),
      content: (
        <>
          <p>{t('billing.actions.renewModalContent')}</p>
          <ul>
            <li>{t('billing.actions.renewLine1')}</li>
            <li>{t('billing.actions.renewLine2')}</li>
          </ul>
        </>
      ),
    },
  };

  return (
    <>
      <Dropdown menu={{ items }} trigger={['click']} placement="bottomRight">
        <MoreOutlined
          style={{
            fontSize: '20px',
            cursor: 'pointer',
          }}
        />
      </Dropdown>

      <Modal
        title={modalContent[actionType].title}
        open={isModalOpen}
        onOk={handleAction}
        onCancel={() => setIsModalOpen(false)}
        okText={modalContent[actionType].okText}
        cancelText={modalContent[actionType].cancelText}
        okButtonProps={{
          danger: actionType === 'cancel',
          loading: loadingState === LoadingState.Loading,
        }}
      >
        {modalContent[actionType].content}
      </Modal>
    </>
  );
};

export default SubscriptionActions;
